/**
 * SkipLink Component
 *
 * Allows keyboard users to skip repetitive navigation and jump directly to main content.
 * Required for WCAG 2.1 Level AA compliance (2.4.1 Bypass Blocks).
 *
 * The link is hidden until focused with Tab key, then appears at the top of the page.
 *
 * Usage:
 * 1. Add <SkipLink /> at the very top of your app
 * 2. Add id="main-content" to your main content area
 *
 * Example:
 * <SkipLink />
 * <nav>...</nav>
 * <main id="main-content">
 *   ...content...
 * </main>
 */

import React from 'react';

interface SkipLinkProps {
  href?: string;
  children?: React.ReactNode;
}

export function SkipLink({
  href = '#main-content',
  children = 'Skip to main content'
}: SkipLinkProps) {
  return (
    <a
      href={href}
      className="skip-link"
      style={{
        position: 'absolute',
        left: '-9999px',
        zIndex: 9999,
        padding: '1rem 1.5rem',
        backgroundColor: '#1e40af', // Darker blue for 4.5:1 contrast (was #2563eb = 2.46:1 FAIL)
        color: 'white',
        textDecoration: 'none',
        borderRadius: '0.25rem',
        fontWeight: '600',
        fontSize: '0.875rem',
        // Show when focused
        outline: '2px solid transparent',
        outlineOffset: '2px',
      }}
      onFocus={(e) => {
        e.currentTarget.style.left = '1rem';
        e.currentTarget.style.top = '1rem';
      }}
      onBlur={(e) => {
        e.currentTarget.style.left = '-9999px';
      }}
    >
      {children}
    </a>
  );
}

export default SkipLink;
